/**
 * @file logo.tsx
 */

"use client";

import { cn } from "visuais/fmt";

interface GlowingSVGProps {
  color?: string;
  className?: string;
  width?: number | string;
  height?: number | string;
}

/**
 * @returns {JSX.Element} Logo com efeito glow.
 * @example
 * ```tsx
 * <Logo color="red" className="w-20 h-20" />
 * ```
 */
export const Logo = ({
  color = "var(--foreground)",
  className,
  width = 403,
  height = 391,
}: GlowingSVGProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 403 391"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(className)}
      style={{ color }}
    >
      <path
        d="M164.413 133.968C175.186 127.978 187.479 125.28 199.77 126.207C212.062 127.135 223.81 131.648 233.562 139.187C243.314 146.726 250.639 156.961 254.632 168.622C258.625 180.284 259.109 192.86 256.024 204.794C252.94 216.728 246.422 227.495 237.279 235.761C228.136 244.027 216.768 249.43 204.585 251.3C192.402 253.17 179.937 251.424 168.736 246.28C157.535 241.136 148.089 232.819 141.567 222.359L139.968 219.644L138.506 216.853C131.367 202.351 130.076 185.661 134.898 170.234C139.72 154.806 150.287 141.823 164.413 133.968Z"
        fill="currentColor"
      />
      <path
        d="M69.6735 258.838C71.3597 257.64 73.2654 256.785 75.2817 256.324C77.2981 255.862 79.3856 255.803 81.4251 256.148C83.4646 256.493 85.416 257.237 87.1681 258.336C88.9201 259.436 90.4384 260.87 91.6363 262.556C103.944 279.886 120.436 293.82 139.578 303.062C158.719 312.304 179.887 316.553 201.112 315.415C203.179 315.303 205.247 315.598 207.199 316.285C209.151 316.972 210.948 318.037 212.489 319.418C214.029 320.8 215.282 322.472 216.177 324.337C217.071 326.203 217.59 328.227 217.702 330.294C217.814 332.36 217.519 334.428 216.832 336.38C216.145 338.332 215.08 340.129 213.698 341.67C212.317 343.21 210.645 344.464 208.779 345.358C206.913 346.253 204.889 346.771 202.823 346.883C176.287 348.309 149.822 342.998 125.89 331.444C101.959 319.889 81.341 302.468 65.9549 280.8C63.5404 277.395 62.5766 273.17 63.2753 269.055C63.974 264.939 66.278 261.269 69.6811 258.851L69.6735 258.838Z"
        fill="currentColor"
      />
      <path
        d="M318.819 120.198L320.476 119.385C322.383 118.584 324.429 118.168 326.498 118.158C328.566 118.149 330.617 118.547 332.531 119.33C334.446 120.113 336.187 121.265 337.657 122.721C339.126 124.177 340.294 125.908 341.094 127.816C351.376 152.318 355.292 179.023 352.477 205.445C349.663 231.867 340.209 257.148 324.996 278.934C323.811 280.63 322.304 282.075 320.56 283.188C318.817 284.301 316.871 285.059 314.834 285.42C312.798 285.781 310.71 285.738 308.69 285.292C306.671 284.846 304.758 284.006 303.063 282.822C301.368 281.637 299.922 280.13 298.809 278.386C297.697 276.643 296.938 274.697 296.577 272.66C296.216 270.624 296.26 268.536 296.706 266.516C297.152 264.497 297.991 262.584 299.176 260.889C311.342 243.463 318.903 223.242 321.154 202.108C323.405 180.975 320.275 159.615 312.053 140.017C310.555 136.458 310.417 132.474 311.665 128.82C312.912 125.166 315.458 122.098 318.819 120.198Z"
        fill="currentColor"
      />
      <path
        d="M46.9761 131.255C60.38 97.4536 85.0666 69.3313 116.846 51.6609C148.626 33.9905 185.541 27.8606 221.326 34.3114C223.387 34.6498 225.361 35.3947 227.131 36.5029C228.901 37.611 230.434 39.0603 231.639 40.7662C232.844 42.4721 233.698 44.4007 234.15 46.4397C234.603 48.4787 234.646 50.5874 234.276 52.643C233.906 54.6986 233.131 56.6601 231.995 58.4133C230.86 60.1666 229.388 61.6765 227.664 62.8553C225.939 64.034 223.998 64.8581 221.952 65.2795C219.907 65.7009 217.797 65.7112 215.748 65.3097C187.116 60.1455 157.579 65.0488 132.152 79.1872C106.724 93.3255 86.973 115.828 76.251 142.874C75.5104 144.827 74.3887 146.613 72.9513 148.128C71.5138 149.643 69.7894 150.858 67.8782 151.7C65.9671 152.543 63.9075 152.997 61.8192 153.036C59.7309 153.075 57.6557 152.698 55.7144 151.928C53.7731 151.157 52.0044 150.009 50.5113 148.548C49.0181 147.088 47.8304 145.345 47.0172 143.421C46.204 141.497 45.7816 139.431 45.7746 137.342C45.7675 135.253 46.1759 133.184 46.9761 131.255Z"
        fill="currentColor"
      />
      <path
        d="M90.31 198.149C94.5848 199.882 95.3864 205.59 91.7543 208.433L44.1012 245.742C40.4621 248.591 35.1035 246.429 34.4607 241.852L26.0277 181.803C25.3849 177.226 29.9407 173.672 34.2238 175.409L90.31 198.149Z"
        fill="currentColor"
      />
      <path
        d="M265.833 112.273C261.542 113.965 257.026 110.384 257.696 105.82L266.487 45.9412C267.159 41.3684 272.531 39.2398 276.152 42.1117L323.663 79.791C327.284 82.6628 326.435 88.3785 322.135 90.0738L265.833 112.273Z"
        fill="currentColor"
      />
      <path
        d="M250.639 347.295C246.665 349.638 241.644 346.808 241.591 342.196L240.891 281.679C240.838 277.058 245.81 274.114 249.836 276.383L302.664 306.152C306.691 308.421 306.748 314.199 302.767 316.547L250.639 347.295Z"
        fill="currentColor"
      />
    </svg>
  );
};
